import { LoaderIcon, TrashIcon } from 'lucide-react'
import { useState } from 'react'

import { MarketOrderDto, useCancelClientMarketOrder } from '@/gen/publicApi'

import { Button } from '@/components/button'
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/dialog'
import { toast } from '@/components/toast/useToast.ts'

import { randomStr } from '@/utils/random.ts'

import IconButton from '../iconButton'

interface OrderCancelDialogProps {
    order: MarketOrderDto
}

export default function OrderCancelDialog({ order }: OrderCancelDialogProps) {
    const [open, setOpen] = useState(false)

    const newClientOrderRef = randomStr(10)
    const cancelOrderMutator = useCancelClientMarketOrder(
        order.clientCode,
        order.marketCode,
        order.clientOrderRef,
        newClientOrderRef,
        {
            mutation: {
                onSuccess: () => {
                    toast({
                        title: 'Successfully cancelled order',
                    })
                    setOpen(false)
                },
                onError: () => {
                    toast({
                        variant: 'destructive',
                        title: 'Error cancelling order',
                    })
                },
            },
        },
    )

    const cancelClick = () => {
        toast({
            title: 'Cancelling order...',
        })

        const cancelBody = { symbol: order.instrumentSymbol }
        cancelOrderMutator.mutate(cancelBody)
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <IconButton
                    icon={TrashIcon}
                    tooltip="Cancel Order"
                    className="text-destructive hover:text-destructive"
                />
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Cancel Order</DialogTitle>
                    <DialogDescription>Are you sure you want to cancel this order?</DialogDescription>
                </DialogHeader>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button type="button" variant="outline" disabled={cancelOrderMutator.isPending}>
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button onClick={cancelClick} variant="destructive" disabled={cancelOrderMutator.isPending}>
                        {cancelOrderMutator.isPending && <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />}
                        Yes
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
